import { boolean, object, string, ref } from 'yup';
import { DateTime } from 'luxon';

const translations = {
  required: (t) => t('formErrors.required'),
  max: (t, value) => t('formErrors.maxLength', { value }),
  phone: (t) => t('formErrors.phone'),
  email: (t) => t('formErrors.email'),
  onlyLetters: (t) => t('formErrors.onlyLetters'),
  invalidDateFormat: (t) => t('formErrors.invalidDateFormat'),
  futureDateError: (t) => t('formErrors.futureDateError'),
  mustBeAdult: (t) => t('formErrors.mustBeAdult'),
  matchEmail: (t) => t('formErrors.matchEmail'),
};

const email = (t) =>
  string()
    .trim()
    .required(translations.required(t))
    .email(translations.email(t));

export const paymentFormValidationSchema = (
  t,
  { fields, withEmailConfirmation = false },
) => {
  const validationFields = fields.reduce((acc, field) => {
    let fieldValidation = string().trim().max(100, translations.max(t, 100));

    if (field.isRequired) {
      fieldValidation = fieldValidation.required(translations.required(t));
    }

    // if (field.type === 'text:phone') {
    //   fieldValidation = fieldValidation.test(
    //     'is-valid-phone',
    //     translations.phone(t),
    //     (value) => {
    //       try {
    //         const parsedPhoneNumber = parsePhoneNumber(value);
    //         return parsedPhoneNumber.isValid();
    //       } catch (error) {
    //         return false;
    //       }
    //     },
    //   );
    // }

    if (field.type === 'text:email') {
      fieldValidation = fieldValidation.email(translations.email(t));
    }

    return {
      ...acc,
      [field.key]: fieldValidation,
    };
  }, {});

  if (withEmailConfirmation) {
    validationFields['email-confirmation'] = string()
      .trim()
      .max(100, translations.max(t, 100))
      .required(translations.required(t))
      .email(translations.email(t))
      .oneOf([ref('email')], translations.matchEmail(t));
  }

  return object(validationFields);
};

export const stripeFormValidationSchema = (t) => {
  return object({
    terms: boolean().oneOf([true], translations.required(t)),
  });
};

// Auth
export const authEmailValidationSchema = (t) => {
  return object({
    email: email(t),
  });
};

export const contactFormValidationSchema = (t) => {
  return object({
    name: string()
      .required(translations.required(t))
      .max(100, translations.max(t, 100)),
    email: string()
      .trim()
      .required(translations.required(t))
      .email(translations.email(t))
      .max(100, translations.max(t, 100)),
    confirmation_number: string().max(100, translations.max(t, 100)),
    message: string()
      .required(translations.required(t))
      .max(3000, translations.max(t, 3000)),
    topic_id: string().required(translations.required(t)),
  });
};

export const passengersFormValidationSchema = (t, { passenger }) => {
  const validationFields = passenger.fields.reduce((acc, field) => {
    let fieldValidation = string().trim();

    if (field.isRequired) {
      fieldValidation = fieldValidation.required(translations.required(t));
    }

    if (field.type === 'text:name') {
      fieldValidation = fieldValidation
        .matches(/^[a-z][a-z`'’,. -]*$/i, translations.onlyLetters(t))
        .max(100, translations.max(t, 100));
    }

    if (field.type === 'text:date.birth') {
      fieldValidation = fieldValidation
        .matches(/^\d{4}-\d{2}-\d{2}$/, translations.invalidDateFormat(t))
        .test('isNotFutureDate', translations.futureDateError(t), (value) => {
          if (!value) return true;
          const enteredDate = DateTime.fromISO(value);
          const today = DateTime.now();
          return enteredDate <= today;
        });
    }

    return { ...acc, [field.key]: fieldValidation };
  }, {});

  return object(validationFields);
};
export const cartPassengersFormValidationSchema = (t, { passengers }) => {
  const validationFields = passengers.reduce(
    (passengerAcc, passenger, passengerIndex) => {
      const passengerFields = passenger.fields.reduce((fieldAcc, field) => {
        let fieldValidation = string().trim();
        const fieldName = `passenger-${passengerIndex}-${field.key}`;

        if (field.isRequired) {
          fieldValidation = fieldValidation.required(translations.required(t));
        }

        if (field.type === 'text:name') {
          fieldValidation = fieldValidation
            .matches(/^[a-z][a-z`'',. -]*$/i, translations.onlyLetters(t))
            .max(100, translations.max(t, 100));
        }

        if (field.type === 'text:date.birth') {
          fieldValidation = fieldValidation
            .matches(/^\d{4}-\d{2}-\d{2}$/, translations.invalidDateFormat(t))
            .test(
              'isNotFutureDate',
              translations.futureDateError(t),
              (value) => {
                if (!value) return true;
                const enteredDate = DateTime.fromISO(value);
                const today = DateTime.now();
                return enteredDate <= today;
              },
            );
        }

        return { ...fieldAcc, [fieldName]: fieldValidation };
      }, {});

      return { ...passengerAcc, ...passengerFields };
    },
    {},
  );

  return object(validationFields);
};

export const refundFormValidationSchema = () => {
  return object({
    agreement: boolean().oneOf([true]),
  });
};

export const searchSubscriptionValidationSchema = (t) => {
  return object({
    email: string()
      .trim()
      .required(translations.required(t))
      .email(translations.email(t))
      .max(100, translations.max(t, 100)),
  });
};
